import { useContext, useEffect, useState } from 'react'
import { Loader } from '@entur/loader'
import { DataCell, HeaderCell, Table, TableBody, TableHead, TableRow } from '@entur/table'
import { ActionChip } from '@entur/chip'
import { AddIcon, DeleteIcon, EditIcon } from '@entur/icons'
import { BannerAlertBox } from '@entur/alert'
import { IconButton } from '@entur/button'
import { space } from '@entur/tokens'

import { Fetch, FetchState, FipTicket } from '../../api-clients/api-types'
import { useApiOnLoad } from '../../api-clients/client-hooks'
import { getFIPTickets } from '../../api-clients/eba-clients'
import { useEmployee } from '../../contexts/employee-context'
import { toPrintFormat } from '../../utils/date-utils'
import { ModalState } from '../../types'
import { NoResult } from '../no-result/no-result'
import FipModal from './fip-modal'
import noResultImg from './no-results.jpg'
import DeleteFIPModal from './delete-fip-modal'
import { useClientInfo } from '../../contexts/client-info-context'
import { ReadOnlyContext } from '../../contexts/read-only-context'

const FipTab = () => {
  const { employee } = useEmployee()
  const { hasSensitiveAccess } = useClientInfo()
  const { readOnly } = useContext(ReadOnlyContext)
  const [modalSate, setModalState] = useState(ModalState.CLOSED)
  const [response] = useApiOnLoad<Array<FipTicket>>(getFIPTickets, {
    id: employee.id,
  })
  const [state, setState] = useState<Fetch<FipTicket[]>>(response)
  const [selectedFipTicket, setSelectedFipTicket] = useState<FipTicket>()

  const truncate = (str: string, n: number) => {
    return str.length > n ? str.slice(0, n - 1) + '…' : str
  }

  const closeModal = (newTicket?: FipTicket) => {
    setModalState(ModalState.CLOSED)
    if (newTicket && state.status === FetchState.SUCCESS) {
      let updatedData = [...state.data].filter((it) => it.ticketId !== newTicket.ticketId)
      if (!newTicket.isDeleted) {
        updatedData = [...updatedData, newTicket]
      }
      setState({
        ...state,
        data: updatedData,
      })
    }
  }

  useEffect(() => {
    setState(response)
  }, [response])

  return (
    <div className="fip-tab" data-cy="fip-tab">
      {!readOnly && hasSensitiveAccess && (
        <ActionChip
          id="new-fip-btn"
          data-cy="new-fip-button"
          style={{ marginBottom: '1rem' }}
          onClick={() => {
            setSelectedFipTicket(undefined)
            setModalState(ModalState.EDIT)
          }}
        >
          <AddIcon />
          Legg til kupong
        </ActionChip>
      )}
      {modalSate === ModalState.EDIT && state.status === FetchState.SUCCESS && (
        <FipModal
          fipTicket={selectedFipTicket}
          fipTickets={state.data}
          open={modalSate === ModalState.EDIT}
          close={closeModal}
        />
      )}
      {selectedFipTicket && (
        <DeleteFIPModal open={modalSate === ModalState.DELETE} fipTicket={selectedFipTicket} close={closeModal} />
      )}
      {state.status === FetchState.LOADING && <Loader />}
      {state.status === FetchState.SUCCESS && state.data.length > 0 && (
        <Table spacing="default">
          <TableHead>
            <TableRow>
              <HeaderCell>Innehaver</HeaderCell>
              <HeaderCell>Billettype</HeaderCell>
              <HeaderCell>Billettnr</HeaderCell>
              <HeaderCell>Utstedt</HeaderCell>
              <HeaderCell>Gyldig</HeaderCell>
              <HeaderCell>Reisetype</HeaderCell>
              <HeaderCell>Kommentar</HeaderCell>
              {!readOnly && hasSensitiveAccess && <HeaderCell> </HeaderCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...state.data]
              .sort((a, b) => (a.issueDate > b.issueDate ? -1 : 1))
              .filter((it) => !it.isDeleted)
              .map((fipTicket) => (
                <TableRow key={`${fipTicket.ticketNo}-${fipTicket.issueDate}-${fipTicket.firstName}`}>
                  <DataCell>
                    {fipTicket.surname}, {fipTicket.firstName}
                  </DataCell>
                  <DataCell>{fipTicket.fipTicketTypeSimple?.code}</DataCell>
                  <DataCell>{fipTicket.ticketNo}</DataCell>
                  <DataCell>{toPrintFormat(fipTicket.issueDate)}</DataCell>
                  <DataCell>
                    {toPrintFormat(fipTicket?.validFrom)} - {toPrintFormat(fipTicket?.validUntil)}
                  </DataCell>
                  <DataCell>
                    {fipTicket.travelType?.code}
                    {fipTicket.travelType?.description && ` - ${fipTicket.travelType?.description}`}
                  </DataCell>
                  <DataCell>{fipTicket?.note != null && truncate(fipTicket.note, 10)}</DataCell>
                  {!readOnly && hasSensitiveAccess && (
                    <DataCell>
                      <IconButton
                        data-cy="edit-fip-button"
                        size="small"
                        style={{ float: 'right' }}
                        onClick={() => {
                          setSelectedFipTicket(fipTicket)
                          setModalState(ModalState.EDIT)
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        data-cy="delete-fip-button"
                        size="small"
                        style={{ float: 'right', marginRight: space.extraSmall }}
                        onClick={() => {
                          setSelectedFipTicket(fipTicket)
                          setModalState(ModalState.DELETE)
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </DataCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}
      {state.status === FetchState.SUCCESS && state.data.length === 0 && (
        <NoResult
          imgSrc={noResultImg}
          imageHeight="300px"
          title="Her var det visst ingen ennå!"
          text="FIP-kuponger som blir laget vil havne i denne fanen."
        />
      )}
      {state.status === FetchState.ERROR && (
        <BannerAlertBox
          variant="error"
          title="Kan ikke hente FIP-billetter"
          closable={false}
          style={{ marginBottom: '1em' }}
        >
          Det har skjedd en teknisk feil ved uthenting av FIP-billetter, ta kontakt med Entur support
        </BannerAlertBox>
      )}
    </div>
  )
}

export default FipTab
